import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { movies, featuredIds } from '@/mocks/movies';

const featured = featuredIds
  .map((id) => movies.find((m) => m.id === id))
  .filter((m): m is (typeof movies)[number] => Boolean(m));

const ROTATE_MS = 6500;

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, []);

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + featured.length) % featured.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 48) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const current = featured[index];

  return (
    <section
      className="relative w-full h-[560px] md:h-[680px] overflow-hidden bg-background-50"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {featured.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 焦点图`}
            title={`${movie.title} - 十八禁影视大全`}
            className={`w-full h-full object-cover object-center ${i === index ? 'animate-slow-zoom' : ''}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-black/40" />

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-2xl">
          <div key={current.id} className="animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-primary-500 text-foreground-950 text-xs px-2.5 py-1 rounded-md font-label">
                {current.type === 'movie' ? 'FOCUS · 电影' : 'FOCUS · 剧集'}
              </span>
              <span className="text-xs text-foreground-700">
                {current.year} · {current.region}
              </span>
              <span className="flex items-center gap-1 text-xs text-accent-400 font-bold">
                <i className="ri-star-fill" />
                {current.rating.toFixed(1)}
              </span>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              {current.title}
            </h1>

            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed line-clamp-3 max-w-xl">
              {current.synopsis}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {current.genres.map((g) => (
                <span
                  key={g}
                  className="text-xs text-foreground-800 bg-background-100/70 border border-background-300/40 px-2.5 py-1 rounded-md backdrop-blur-sm"
                >
                  {g}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm rounded-md px-6 py-3 whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-play-circle-line text-lg" />
                查看详情
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 bg-background-100/40 hover:bg-background-100/70 border border-background-300/50 text-foreground-900 text-sm rounded-md px-6 py-3 whitespace-nowrap cursor-pointer backdrop-blur-sm transition-colors"
              >
                <i className="ri-compass-3-line text-lg" />
                浏览全部片库
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-10 left-4 md:left-8 z-20 flex items-center gap-4">
        <div className="flex items-center gap-2">
          {featured.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到 ${movie.title}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-8 bg-primary-500' : 'w-3 bg-background-300/70 hover:bg-background-400'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-10 right-4 md:right-8 z-20 flex items-center gap-2">
        <button
          type="button"
          aria-label="上一部"
          onClick={() => go(-1)}
          className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-background-100/60 hover:bg-background-100/90 backdrop-blur-sm text-foreground-900 flex items-center justify-center cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => go(1)}
          className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-background-100/60 hover:bg-background-100/90 backdrop-blur-sm text-foreground-900 flex items-center justify-center cursor-pointer transition-colors"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </section>
  );
}